// @flow
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Input,
  Icon,
  Tooltip,
} from 'antd';
import { SpH5 } from 'components/DesignKit';
import { useLocation } from 'react-router-dom';

import Loading from 'components/Loading';
import formatNumber from 'utils/formatNumber';
import CheckboxMenu from 'utils/checkboxMenu';
import DateMenu from 'utils/dateMenu';
import 'assets/css/reportingPageStyle.css';

import {
  PAGES,
} from 'appconstants';

import {
  reportingFunding,
  filterParamsActions,
  payoutsActions,
} from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';
import PaidPayout from 'components/NavIcons/payoutPaid';
import DisablePayout from 'components/NavIcons/disablePayoutPaid';
import moment from 'moment-timezone';
import ExportIcon from 'components/NavIcons/exportIcon';
import UpdatePayout from './UpdatePayout';

type Props = {
  fetchFundingReporting: Function,
  fundingsReport: Array<Object>,
  loading: boolean,
  test: boolean,
  history: {
    push: Function,
  },
  selectedAccount: Object,
  submitting: boolean,
  updatePayout: Function,
};

const FundingReporting = (props: Props) => {
  const {
    loading,
    fetchFundingReporting,
    fundingsReport,
    test,
    history,
    selectedAccount,
    submitting,
    updatePayout,
  } = props;

  const location = useLocation();
  const [searchMethos, setSearchMethos] = useState('');
  const [selectedM, setSelectedM] = useState([]);
  const currencyType = selectedAccount && selectedAccount.currency;
  const [searchValue, setSearchValue] = useState('');
  const [filterdRecods, setFilterdRecords] = useState([]);
  const [searchDate, setSearchDate] = useState('');
  const [updatePayoutModal, setUpdatePayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState({});
  const [fillColor, setFillColor] = useState(false);

  const debouncedFetchTransactions = debounce(fetchFundingReporting, 2000, { leading: true });

  const getData = (query) => {
    debouncedFetchTransactions({ query });
  };

  useEffect(() => {
    if (location.search) {
      getData(location.search);
    }
  }, [location]);

  useEffect(() => {
    const tz = selectedAccount?.timezone;
    const currentEndDate = moment()
      .tz(tz);
    const currentStartDate = moment(currentEndDate)
      .tz(tz)
      .startOf('day');
    const format = 'YYYY-MM-DDTHH:mm:ss';
    const end = moment(currentEndDate).utc().format(format);
    const start = moment(currentStartDate).utc().format(format);
    getData(`?payoutDate.AF=${start}&payoutDate.BF=${end}`);
  }, [test, selectedAccount]);

  const showUpdatePayoutModal = (value: Object) => {
    setSelectedPayout(value);
    setUpdatePayoutModal(true);
  };

  useEffect(() => {
    setFilterdRecords(fundingsReport);
    setSearchValue('');
  }, [fundingsReport]);

  const tableHeader = (title) => (
    <span
      style={{
        fontWeight: 'bold',
        fontSize: '14px',
        color: '#333333',
      }}
    >
      {title}
    </span>
  );

  const exportHeaders = [
    { label: 'AMOUNT', key: 'amount' },
    { label: 'STATUS', key: 'status' },
    { label: 'TYPE', key: 'type' },
    { label: 'DESTINATION', key: 'destination' },
    { label: 'DATE', key: 'paymentDate' },
  ];

  const exportData = filterdRecods ? filterdRecods.map((values) => ({
    amount: `${selectedAccount.currency.prefix}${formatNumber((values.amount / 100).toFixed(2), currencyType)}`,
    status: values?.status,
    type: values?.type,
    destination: `${values?.bankName} ********${values?.bankAccountLastFour}` || '--',
    paymentDate: values.payoutDate ? moment
      .utc(values.payoutDate)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY') : null,
  })) : '';

  const columns = [
    {
      title: tableHeader('AMOUNT'),
      dataIndex: 'amount',
      width: '20%',
      align: 'left',
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <span style={{ cursor: 'pointer' }}>
            {`${selectedAccount.currency.prefix} ${formatNumber(total, currencyType)}`}
          </span>
        );
      },
    },
    {
      title: tableHeader('STATUS'),
      dataIndex: 'status',
      width: '20%',
      align: 'left',
      render: (status) => (
        <span style={{ cursor: 'pointer' }}>
          {status || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('TYPE'),
      dataIndex: 'type',
      width: '10%',
      align: 'left',
      render: (type) => (
        <span style={{ cursor: 'pointer' }}>
          {type || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('DESTINATION'),
      dataIndex: 'type',
      width: '20%',
      align: 'left',
      render: (text, record) => (
        <span style={{ cursor: 'pointer' }}>
          {`${record?.bankName} ********${record?.bankAccountLastFour}` || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('DATE'),
      dataIndex: 'payoutDate',
      width: '20%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (payoutDate) => {
        if (payoutDate && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(payoutDate)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
    {
      title: tableHeader('ACTION'),
      dataIndex: 'status',
      width: '10%',
      align: 'left',
      render: (text, record) => {
        const { status, type } = record;
        if ((status === 'IN_TRANSIT') && (type !== 'WALLET')) {
          return (
            <span>
              <Icon
                component={PaidPayout}
                style={{ fontSize: '24px', marginRight: '8px', cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); showUpdatePayoutModal(record); }}
              />
            </span>
          );
        }
        return (
          <span>
            <Icon
              component={DisablePayout}
              style={{ fontSize: '24px', marginRight: '8px', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); }}
            />
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    if (searchMethos && !searchDate) {
      history.push({
        pathname: PAGES.PAYMENTS_FUNDING,
        search: `?${searchMethos}`,
      });
    }
    if (!searchMethos && searchDate) {
      history.push({
        pathname: PAGES.PAYMENTS_FUNDING,
        search: `?${searchDate}`,
      });
    }
    if (searchMethos && searchDate) {
      history.push({
        pathname: PAGES.PAYMENTS_FUNDING,
        search: `?${searchMethos}&${searchDate}`,
      });
    }
    if (!searchMethos && !searchDate) {
      history.push({
        pathname: PAGES.PAYMENTS_FUNDING,
        search: '',
      });
    }
  }, [searchMethos, searchDate]);

  const payoutStatuses = [
    'PENDING', 'IN_TRANSIT', 'PAID', 'WITHDRAWN', 'FAILED', 'PARTIAL_REFUNDED', 'REFUNDED',
  ];

  const onMethodChange = (selection) => {
    const searchMethod = [];
    for (let i = 0; i < selection.length; i += 1) {
      searchMethod.push(`${'status.IN'}=${selection[i]}`);
    }
    setSearchMethos(searchMethod.toString().replaceAll(',', '&'));
    setSelectedM(selection);
  };

  const clearMethods = () => {
    setSearchMethos('');
    setSelectedM([]);
  };

  const searchMethods = () => {
    const searchMethod = [];
    for (let i = 0; i < payoutStatuses.length; i += 1) {
      searchMethod.push(`${'status.IN'}=${payoutStatuses[i]}`);
    }
    setSearchMethos(searchMethod.toString().replaceAll(',', '&'));
    setSelectedM(payoutStatuses);
  };

  const handleSearch = (searchText) => {
    setSearchValue(searchText);
    setFilterdRecords(fundingsReport.filter((data) => JSON.stringify(data).toLowerCase().indexOf(searchText.toLowerCase()) !== -1));
  };

  const clearDates = () => {
    setSearchDate('');
    getData();
  };

  const selectedRecord = (record) => {
    const { gid } = record;
    if (gid) {
      history.push({
        pathname: `${PAGES.PAYOUTS}/${gid}`,
        state: location.search,
      });
    }
  };

  const searchFloatingDate = (floatingType) => {
    const tz = selectedAccount?.timezone;
    let currentEndDate = moment()
      .tz(tz)
      .subtract(15, 'minutes');
    let currentStartDate = moment(currentEndDate)
      .tz(tz)
      .startOf('day');
    let prevEndDate = moment(currentStartDate)
      .tz(tz)
      .subtract(15, 'minutes');
    let prevStartDate = moment(prevEndDate)
      .tz(tz)
      .startOf('day');
    const format = 'YYYY-MM-DDTHH:mm:ss';
    if (floatingType === 'Today') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
    }
    if (floatingType === 'Day') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .startOf('day');
      const end = moment(prevEndDate).utc().format(format);
      const start = moment(prevStartDate).utc().format(format);
      setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
    }
    if (floatingType === '7Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(6, 'day')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(6, 'day')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
    }
    if (floatingType === '30Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(29, 'day')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(29, 'day')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
    }
    if (floatingType === '90Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(3, 'month')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(3, 'month')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
    }
    if (floatingType === 'Reset') {
      setSearchDate('');
      getData();
    }
  };

  const searchSpecificDate = (startDate, endDate) => {
    const tz = selectedAccount?.timezone;
    const currentEndDate = moment(endDate)
      .tz(tz)
      .endOf('day');
    const currentStartDate = moment(startDate)
      .tz(tz)
      .startOf('day');
    const format = 'YYYY-MM-DDTHH:mm:ss';
    const end = moment(currentEndDate).utc().format(format);
    const start = moment(currentStartDate).utc().format(format);
    setSearchDate(`payoutDate.AF=${start}&payoutDate.BF=${end}`);
  };

  const updatePayoutStatus = (payoutData: Object) => {
    const {
      gid,
      referenceNumber,
      ref1,
      ref2,
      ref3,
      ref4,
    } = payoutData;
    const params = {
      referenceNumber,
      ref1: ref1 || null,
      ref2: ref2 || null,
      ref3: ref3 || null,
      ref4: ref4 || null,
    };
    updatePayout({
      params,
      gid,
    });
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card
      style={{
        padding: '0',
        borderWidth: '0',
      }}
      className="reporting_summary_card"
    >
      <Row
        type="flex"
        justify="space-between"
        // className="my-2 pb-2"
        className="mb-4 reporting_summary_filter_bar"
        align="middle"
        style={{
          backgroundColor: '#fbfbfb',
          borderBottom: '1px solid #e0e0e0',
          minHeight: '40px',
        }}
      >
        <Col>
          <Row type="flex" justify="space-between" align="middle">
            <Col className="mr-4 ml-4 "><SpH5>Search By</SpH5></Col>
            {/* <Col className="mr-5"> */}
            {/* <label><SpH5>Payout Status</SpH5></label> */}
            <Col>
              {/* <label><SpH5>Date</SpH5></label> */}
              <Input.Group compact placeholder="Date">
                <DateMenu
                  value={selectedM}
                  clearAll={clearDates}
                  searchAll={searchFloatingDate}
                  searchSelected={searchSpecificDate}
                  title="Date"
                />
              </Input.Group>
            </Col>
            <Col>
              <Input.Group compact placeholder="Method">
                <CheckboxMenu
                  options={payoutStatuses}
                  value={selectedM}
                  onChange={onMethodChange}
                  clearAll={clearMethods}
                  searchAll={searchMethods}
                  title="Payout Status"
                />
              </Input.Group>
            </Col>
          </Row>
        </Col>
        <Col
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '10px',
          }}
        >
          <span>
            {
              exportData !== null
                ? (
                  <Tooltip placement="left" title="Click here to export Fundings">
                    <CSVLink
                      target="_blank"
                      className="mr-3"
                      filename="Fundings Report.csv"
                      data={exportData}
                      headers={exportHeaders}
                      onMouseEnter={() => setFillColor(true)}
                      onMouseLeave={() => setFillColor(false)}
                    >
                      <ExportIcon
                        fill={fillColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </CSVLink>
                  </Tooltip>
                ) : null
            }
          </span>
          <Input.Search
            // className="mr-3 mt-3"
            className="reporting_search_input"
            style={{
              width: 300,
              maxHeight: '35px',
            }}
            value={searchValue}
            placeholder="Search All"
            onChange={(e) => handleSearch((e.target.value))}
          />
        </Col>
      </Row>
      {/* UPDATE PAYOUT MODAL */}
      {updatePayoutModal && (
        <UpdatePayout
          visible={updatePayoutModal}
          selectedPayout={selectedPayout}
          selectedAccount={selectedAccount}
          submitting={submitting}
          currencyType={currencyType}
          close={() => setUpdatePayoutModal(false)}
          submit={updatePayoutStatus}
        />
      )}
      <Table
        onRow={(record) => ({
          onClick: () => {
            selectedRecord(record);
          },
        })}
        rowClassName={() => 'ant-table-clickable-row'}
        columns={columns}
        rowKey={(record) => record.gid}
        dataSource={filterdRecods}
        pagination={false}
        className="mx-4"
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  fundingsReport: state?.reportingFunding?.entity,
  loading: state.loading.loading,
  test: state.account.test,
  totalElements: state.reportingFunding.totalElements,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchFundingReporting: param => dispatch(reportingFunding.fetchFundingReporting({
    payload: param,
  })),
  updatePayout: param => dispatch(payoutsActions.updatePayout({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(FundingReporting);
